/**
 * Bridges raw sim state into music/SFX triggers. This is the only place
 * that knows about GameState/GameEvent shapes; everything under it works in
 * terms of plain audio calls.
 *
 * Four of the sim's eight GameEvent types map straight to a one-shot cue
 * (underAttack, anomalyStarted, phaseAdvanced, techResearched, gameOver).
 * The rest of the SFX list (train/build complete, serve income, unit death,
 * building destroyed) has no dedicated GameEvent, so it's derived by
 * diffing consecutive GameState snapshots — this module owns that small
 * bit of state entirely on its own, driven by a single onTick(state,
 * myPlayer) call from session.ts.
 */

import type { GameEvent, GameState, PlayerIdx } from '../../sim';
import { notifyUnderAttack, playDefeatStinger, playPhaseUpStinger, playVictoryStinger, updateTensionBudget } from './music';
import {
  playAnomalyAlarm,
  playBuildComplete,
  playBuildingDestroyed,
  playResearchComplete,
  playServeIncome,
  playUnderAttackAlert,
  playUnitDeath,
} from './sfx';

let prevUnitIds = new Set<number>();
let prevBuildingIds = new Set<number>();
let prevBuildingConstructing = new Set<number>();
let lastMyPlayer: PlayerIdx = -999;
let seeded = false;

function handleEvent(ev: GameEvent, myPlayer: PlayerIdx, winner: PlayerIdx | null): void {
  const mine = ev.player === myPlayer;
  switch (ev.type) {
    case 'underAttack':
      if (mine) {
        notifyUnderAttack();
        playUnderAttackAlert();
      }
      break;
    case 'anomalyStarted':
      if (mine) playAnomalyAlarm();
      break;
    case 'phaseAdvanced':
      if (mine) playPhaseUpStinger();
      break;
    case 'techResearched':
      if (mine) playResearchComplete();
      break;
    case 'gameOver':
      if (winner !== null) {
        if (winner === myPlayer) playVictoryStinger();
        else playDefeatStinger();
      }
      break;
    default:
      break;
  }
}

function diffEntities(state: GameState, myPlayer: PlayerIdx, skipEffects: boolean): void {
  const me = state.players[myPlayer];
  const curUnitIds = new Set<number>();
  const curBuildingIds = new Set<number>();
  const curBuildingConstructing = new Set<number>();
  let anyServing = false;

  for (const e of state.entities) {
    if (e.owner !== myPlayer) continue;
    if (e.kind === 'unit') {
      curUnitIds.add(e.id);
      if (!skipEffects && !prevUnitIds.has(e.id)) playBuildComplete(); // training completed
      if (e.serving) anyServing = true;
    } else if (e.kind === 'building') {
      curBuildingIds.add(e.id);
      if (e.buildTicks > 0) {
        curBuildingConstructing.add(e.id);
      } else if (!skipEffects && prevBuildingConstructing.has(e.id)) {
        playBuildComplete(); // construction completed
      }
    }
  }

  if (!skipEffects) {
    for (const id of prevUnitIds) if (!curUnitIds.has(id)) playUnitDeath();
    for (const id of prevBuildingIds) if (!curBuildingIds.has(id)) playBuildingDestroyed();
    if (me && me.revenuePerSec > 0 && anyServing) playServeIncome();
  }

  prevUnitIds = curUnitIds;
  prevBuildingIds = curBuildingIds;
  prevBuildingConstructing = curBuildingConstructing;
}

/** Reset all snapshot tracking — call when a fresh game/session starts so
 * the first tick's entities don't read as "everything just got built". */
export function resetEventTracking(): void {
  prevUnitIds = new Set();
  prevBuildingIds = new Set();
  prevBuildingConstructing = new Set();
  seeded = false;
}

/** Single per-tick hook: process this tick's discrete events, feed the
 * tension layer the local player's budget, and diff entities for the SFX
 * that have no dedicated event. */
export function onTick(state: GameState, myPlayer: PlayerIdx): void {
  for (const ev of state.events) handleEvent(ev, myPlayer, state.winner);

  const me = state.players[myPlayer];
  if (me) updateTensionBudget(me.budget);

  if (myPlayer !== lastMyPlayer) {
    lastMyPlayer = myPlayer;
    seeded = false;
  }

  diffEntities(state, myPlayer, !seeded);
  seeded = true;
}
